import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import NewsletterCTA from "../home/components/NewsletterCTA";
import { featuredEpisode, comingSoonEpisodes } from "../../mocks/episodes";
import StarRating from "../../components/base/StarRating";
import { useEpisodeRatings } from "../../hooks/useEpisodeRatings";
import AudioPlayer from "../../components/feature/AudioPlayer";

const COVER_ART = "https://static.readdy.ai/image/f9f32838a9120faa908a5852ba765d83/f8910ea58b264f83bfa3194cce1f37cf.png";

const platforms = [
  { label: "Spotify", icon: "ri-spotify-fill", url: "https://podcasters.spotify.com/pod/show/rich" },
  { label: "Apple Podcasts", icon: "ri-apple-fill", url: "#" },
  { label: "Google Podcasts", icon: "ri-google-play-fill", url: "#" },
  { label: "Amazon Music", icon: "ri-amazon-fill", url: "#" },
];

export default function Episodes() {
  const { getRating, rateEpisode } = useEpisodeRatings();

  return (
    <main className="min-h-screen bg-cream font-sans">
      <Navbar />

      {/* Page hero */}
      <section
        className="relative pt-40 pb-20 overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, #3b1505 0%, #6b2a08 50%, #92400e 100%)",
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <span className="inline-block font-sans text-xs font-semibold tracking-widest uppercase text-amber-300 bg-amber-400/20 border border-amber-400/30 rounded-full px-4 py-1.5 mb-6">
            Episodes
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-cream leading-tight mb-5">
            Honest Conversations.<br />
            <em className="text-amber-400">Real Understanding.</em>
          </h1>
          <p className="font-sans text-base text-amber-100/75 max-w-xl mx-auto leading-relaxed">
            Every episode is built for real people — no tech background required. Just clear, grounded conversation about one of the biggest forces shaping our world.
          </p>
        </div>
      </section>

      {/* Featured Episode */}
      <section className="bg-cream py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">

          {/* Section label */}
          <div className="flex items-center gap-4 mb-10">
            <span className="font-sans text-xs font-semibold tracking-widest uppercase text-amber-600">
              {featuredEpisode.comingSoon ? "Coming Soon" : "Now Available"}
            </span>
            <div className="h-px flex-1 bg-amber-100" />
          </div>

          {/* Featured card */}
          <div className="bg-white rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">

              {/* Cover art side */}
              <div
                className="relative flex items-center justify-center p-12 lg:p-16 min-h-[360px]"
                style={{
                  background:
                    "linear-gradient(135deg, #3b1505 0%, #78350f 60%, #a85510 100%)",
                }}
              >
                {/* Glow */}
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(ellipse at 50% 60%, rgba(245,158,11,0.18) 0%, transparent 65%)" }} />

                {/* Cover art */}
                <div className="relative z-10">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={COVER_ART}
                      alt="The Humble Machine Podcast"
                      className="w-64 h-64 lg:w-72 lg:h-72 object-cover"
                    />
                    <div className="absolute inset-0 rounded-2xl ring-1 ring-amber-400/20" />
                  </div>

                  {/* Badge */}
                  <div className="absolute -top-3 -right-3 bg-amber-500 text-amber-950 font-sans font-bold text-xs px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                    Episode 1
                  </div>
                </div>

                {/* Duration pill */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-full px-4 py-2">
                  {featuredEpisode.comingSoon ? (
                    <>
                      <i className="ri-calendar-event-line text-amber-300 text-sm" />
                      <span className="font-sans text-xs text-amber-200 font-medium">Launches {featuredEpisode.launchDate}</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-time-line text-amber-300 text-sm" />
                      <span className="font-sans text-xs text-amber-200 font-medium">{featuredEpisode.duration}</span>
                      <span className="text-amber-600 text-xs mx-1">&bull;</span>
                      <span className="font-sans text-xs text-amber-200 font-medium">{featuredEpisode.date}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Info side */}
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-sans text-xs font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-3 py-1 rounded-full">
                    {featuredEpisode.category}
                  </span>
                  <span className="font-sans text-xs text-stone-400">Ep. {featuredEpisode.number}</span>
                </div>

                <h2 className="font-serif text-2xl lg:text-3xl font-bold text-amber-900 leading-snug mb-5">
                  {featuredEpisode.title}
                </h2>

                <p className="font-sans text-sm text-stone-500 leading-relaxed mb-8">
                  {featuredEpisode.description}
                </p>

                {featuredEpisode.comingSoon ? (
                  /* Coming Soon callout */
                  <div className="flex flex-col gap-4">
                    <div className="inline-flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                        <i className="ri-calendar-check-line text-amber-600 text-lg" />
                      </div>
                      <div>
                        <p className="font-sans font-bold text-amber-900 text-sm">Launching {featuredEpisode.launchDate}</p>
                        <p className="font-sans text-xs text-stone-400 mt-0.5">Subscribe to get notified the moment it drops.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {platforms.map((p) => (
                        <a
                          key={p.label}
                          href={p.url}
                          rel="nofollow"
                          aria-label={`Subscribe on ${p.label}`}
                          className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 hover:bg-amber-200 transition-colors cursor-pointer"
                        >
                          <i className={`${p.icon} text-amber-700 text-base`} />
                        </a>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Inline audio player */}
                    <div className="mb-7">
                      <AudioPlayer
                        src={featuredEpisode.audioUrl}
                        duration={featuredEpisode.duration}
                        variant="full"
                      />
                    </div>

                    {/* Platform row */}
                    <div>
                      <p className="font-sans text-xs text-stone-400 mb-3 uppercase tracking-widest">Also listen on</p>
                      <div className="flex items-center gap-3">
                        {platforms.map((p) => (
                          <a
                            key={p.label}
                            href={p.url}
                            rel="nofollow"
                            aria-label={p.label}
                            title={p.label}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 hover:bg-amber-200 transition-colors cursor-pointer"
                          >
                            <i className={`${p.icon} text-amber-700 text-base`} />
                          </a>
                        ))}
                      </div>
                    </div>
                    {/* Star Rating */}
                    <div className="mt-6 pt-6 border-t border-amber-100">
                      <p className="font-sans text-xs text-stone-400 uppercase tracking-widest mb-3">Rate This Episode</p>
                      <div className="flex items-center gap-4">
                        <StarRating
                          value={getRating(featuredEpisode.id)}
                          onChange={(rating) =>
                            rateEpisode(
                              featuredEpisode.id,
                              featuredEpisode.title,
                              featuredEpisode.number,
                              rating
                            )
                          }
                          size="lg"
                        />
                        {getRating(featuredEpisode.id) > 0 && (
                          <span className="font-sans text-sm font-semibold text-amber-700">
                            {getRating(featuredEpisode.id)}/5 — Thanks for rating!
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="bg-parchment py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">

          {/* Header */}
          <div className="text-center mb-14">
            <span className="font-sans text-xs font-semibold tracking-widest uppercase text-amber-600 block mb-4">
              Up Next
            </span>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-amber-900 mb-4">
              More Episodes on the Way
            </h2>
            <p className="font-sans text-base text-stone-500 max-w-lg mx-auto leading-relaxed">
              New episodes coming soon. Subscribe so you don&apos;t miss what&apos;s next.
            </p>
          </div>

          {/* Coming soon grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {comingSoonEpisodes.map((ep) => (
              <div
                key={ep.id}
                className="bg-white rounded-2xl overflow-hidden border border-amber-100 hover:-translate-y-1.5 hover:border-amber-200 transition-all duration-300 cursor-default group"
              >
                {/* Placeholder image area */}
                <div
                  className="h-44 flex flex-col items-center justify-center gap-3 relative group-hover:brightness-105 transition-all duration-300"
                  style={{
                    background:
                      "linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fef3c7 100%)",
                  }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-amber-200/70 group-hover:bg-amber-300/70 transition-colors duration-200">
                    <i className={`${ep.icon} text-amber-600 text-2xl`} />
                  </div>
                  <div className="flex items-center gap-1.5 bg-amber-800/15 rounded-full px-3 py-1">
                    <i className="ri-lock-line text-amber-600 text-xs" />
                    <span className="font-sans text-xs font-semibold text-amber-700 uppercase tracking-wide">
                      Coming Soon
                    </span>
                  </div>
                </div>

                {/* Card content */}
                <div className="p-6">
                  <span className="font-sans text-xs text-amber-500 font-semibold tracking-widest block mb-2">
                    Ep. {ep.number} &bull; {ep.teaserTheme}
                  </span>
                  <h3 className="font-serif font-bold text-amber-800 text-lg mb-3">
                    {ep.teaserTitle}
                  </h3>
                  <p className="font-sans text-sm text-stone-400 leading-relaxed italic">
                    Episode details dropping soon. Subscribe to get notified the moment it goes live.
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Subscribe nudge */}
          <div className="mt-14 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white rounded-2xl px-8 py-6 border border-amber-100">
              <div className="w-11 h-11 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                <i className="ri-notification-3-line text-amber-700 text-lg" />
              </div>
              <div className="text-left">
                <p className="font-sans font-semibold text-amber-900 text-sm">Never miss a new episode</p>
                <p className="font-sans text-xs text-stone-400 mt-0.5">Subscribe on your favorite platform and get notified instantly.</p>
              </div>
              <div className="flex items-center gap-2 sm:ml-4">
                {platforms.slice(0, 3).map((p) => (
                  <a
                    key={p.label}
                    href={p.url}
                    rel="nofollow"
                    aria-label={p.label}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${p.icon} text-amber-700 text-base`} />
                  </a>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      <NewsletterCTA />
      <Footer />
    </main>
  );
}
